<template>
  <div v-if="!isConfirmed">
    <div class="form-group">
      <label class="form-label">Date</label>
      <ValidationProvider rules="required" ref="date" v-slot="{ errors }">
        <DateInput v-model="date" />
        <div class="text-danger" v-for="(error, i) of errors" :key="i">{{ error }}</div>
      </ValidationProvider>
    </div>

    <div class="form-group">
      <label class="form-label">Reason</label>
      <ValidationProvider rules="required" ref="reason" v-slot="{ errors }">
        <select class="form-control col-sm-4" v-model="reason">
          <option disabled value="">Please select one</option>
          <option v-for="r of reasons" :key="r.code" :value="r">
            {{ r.name }}
          </option>
        </select>
        <div class="text-danger" v-for="(error, i) of errors" :key="i">{{ error }}</div>
      </ValidationProvider>
    </div>

    <div class="form-group">
      <label class="form-label">Comment</label>
      <ValidationProvider ref="comment" v-slot="{ errors }">
        <Textarea class="form-control" v-model="comment" />
        <div class="text-danger" v-for="(error, i) of errors" :key="i">{{ error }}</div>
      </ValidationProvider>
    </div>
  </div>

  <div v-else>
    <div class="form-group">
      <label>Date</label>
      <div class="form-confirm-value">{{ date | ymd }}</div>
    </div>
    <div class="form-group">
      <label>Reason</label>
      <div class="form-confirm-value">{{ reason.name }}</div>
    </div>
    <div class="form-group">
      <label>Comment</label>
      <div class="form-confirm-value">{{ comment }}</div>
    </div>
    <div class="form-group" v-if="isExecuted()">
      <label>{{ status | status }} Comment</label>
      <div class="form-confirm-value">{{ approvedComment }}</div>
    </div>
  </div>

</template>

<script lang="ts">
import _ from 'lodash'
import moment from 'moment'
import { Component, Prop, Vue } from 'vue-property-decorator'
import { ValidationProvider } from 'vee-validate'
import { VacationReason } from '@/store/master/types'
import { RootState } from '@/store/types'
import { Tutor } from '@/store/tutors/types'
import DateInput from '@/components/parts/inputItem/DateInput.vue'
import Textarea from '@/components/parts/inputItem/Textarea.vue'
import { IAddApplicationComponent } from '@/components/pages/application/Add.vue'
import { VacationApplication } from '@/store/applications/vacations/types'
import { applicationStatusCode, ApplicationStatus } from '@/store/applications/types'

@Component({
  components: {
    ValidationProvider,
    DateInput,
    Textarea,
  },
  filters: {
    ymd(date: Date) {
      return moment(date).format('YYYY/MM/DD')
    },
    status: (s: applicationStatusCode): string => ApplicationStatus[s],
  },
})
export default class ApplicationAddVacation extends Vue implements IAddApplicationComponent {
  @Prop({ required: true }) isConfirmed!: boolean

  // 編集時・詳細時など、初期値が必要な場合
  @Prop() initialValues!: VacationApplication

  @Prop() status!: applicationStatusCode

  private date: Date|null = null

  private reason: VacationReason|null = null

  private comment: string|null = null

  private approvedComment: string|null = null

  private isValidated: boolean = false

  private created() {
    if (this.initialValues) {
      this.date = this.initialValues.date.toDate()
      this.reason = this.initialValues.reason
      this.comment = this.initialValues.comment
      this.approvedComment = this.initialValues?.approvedComment || null
    }

    this.$store.dispatch('fetchMasterVacationReasons')
  }

  private get reasons(): VacationReason[] {
    return (this.$store.state as RootState).master.vacationReasons
  }

  /**
   * バリデーション（ApplicationAdd.vueから呼び出し）
   * @return {Promise} バリデーション結果
   */
  public validation(): Promise<boolean> {
    return Promise.all([
      'date', 'reason', 'comment',
    ].map((key) => (this.$refs[key] as any).validate()))
      .then((validationResults) => {
        const errorLength = _.filter(validationResults, { valid: false }).length

        if (errorLength === 0) {
          this.isValidated = true
          return true
        }
        return false
      })
  }

  /**
   * 登録（ApplicationAdd.vueから呼び出し）
   * @return {Promise} ActionからのPromise
   */
  public register(tutor: Tutor): Promise<void> {
    const application: VacationApplication = {
      type: 'vacation',
      targetTutor: tutor,
      date: moment(this.date),
      reason: this.reason as VacationReason,
      comment: this.comment,
    }

    return this.$store.dispatch('registerVacationApplication', application)
  }

  /**
   * 申請のステータスが未承認以外かチェック
   */
  private isExecuted() {
    return this.status !== 'unapproved'
  }
}
</script>

<style scoped>
</style>
